import { useEffect, useRef, useState } from "react";

type FormState = "idle" | "submitting" | "success" | "error";

const inquiryTypes = [
  "商品諮詢",
  "訂製服務",
  "能量飾品選購建議",
  "訂單相關",
  "合作洽談",
  "其他問題",
];

export default function ContactForm() {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const [formState, setFormState] = useState<FormState>("idle");
  const [charCount, setCharCount] = useState(0);

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    inquiryType: "",
    message: "",
  });

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (name === "message") setCharCount(value.length);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (charCount > 500) return;
    setFormState("submitting");
    try {
      const body = new URLSearchParams();
      body.append("name", form.name);
      body.append("email", form.email);
      body.append("phone", form.phone);
      body.append("inquiryType", form.inquiryType);
      body.append("message", form.message);

      const res = await fetch("https://readdy.ai/api/form/d7g6hac0ok5brd6lp020", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });

      if (res.ok) {
        setFormState("success");
        setForm({ name: "", email: "", phone: "", inquiryType: "", message: "" });
        setCharCount(0);
      } else {
        setFormState("error");
      }
    } catch {
      setFormState("error");
    }
  };

  const inputBase =
    "w-full bg-white border border-[#e8e0d5] rounded-xl px-5 py-3.5 text-[1rem] text-[#2d2520] placeholder-[#b8a898] focus:outline-none focus:border-[#8b6f47] transition-colors";

  return (
    <section className="py-20 bg-[#faf8f4]" ref={ref}>
      <div className="max-w-7xl mx-auto px-6 md:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_420px] gap-16 items-start">

          {/* Left: Form */}
          <div
            className={`transition-all duration-1000 ${
              visible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-8"
            }`}
          >
            <div className="flex items-center gap-3 mb-6">
              <span className="w-10 h-px bg-[#8b6f47]"></span>
              <span
                className="text-[#8b6f47] text-[1rem] tracking-[0.3em] font-medium"
                style={{ fontFamily: "'Noto Serif TC', serif" }}
              >
                SEND MESSAGE
              </span>
            </div>
            <h2
              className="text-[#2d2520] font-bold mb-3 leading-[1.4]"
              style={{
                fontSize: "clamp(1.5rem, 3vw, 2.125rem)",
                fontFamily: "'Noto Serif TC', serif",
              }}
            >
              傳送訊息給我們
            </h2>
            <p
              className="text-[#7a6a5a] leading-[1.75] mb-10"
              style={{
                fontSize: "1.1875rem",
                fontFamily: "'Noto Serif TC', serif",
              }}
            >
              填寫以下表單，我們將在 1–2 個工作天內回覆您。
            </p>

            {formState === "success" ? (
              <div className="bg-white rounded-2xl p-12 text-center border border-[#e8e0d5]">
                <div className="w-16 h-16 flex items-center justify-center rounded-full bg-[#f0e8dc] mx-auto mb-5">
                  <i className="ri-check-line text-[#8b6f47] text-3xl"></i>
                </div>
                <h3
                  className="text-[#2d2520] font-bold mb-3"
                  style={{
                    fontSize: "1.625rem",
                    fontFamily: "'Noto Serif TC', serif",
                  }}
                >
                  訊息已送出！
                </h3>
                <p
                  className="text-[#7a6a5a] leading-[1.75] mb-8"
                  style={{
                    fontSize: "1.1875rem",
                    fontFamily: "'Noto Serif TC', serif",
                  }}
                >
                  感謝您的來信，我們已收到您的訊息，將在 1–2 個工作天內與您聯繫。
                </p>
                <button
                  onClick={() => setFormState("idle")}
                  className="px-8 py-3 bg-[#8b6f47] text-white rounded-full text-[1rem] font-medium cursor-pointer hover:bg-[#7a5f3a] transition-colors whitespace-nowrap"
                  style={{ fontFamily: "'Noto Serif TC', serif" }}
                >
                  再次傳送
                </button>
              </div>
            ) : (
              <form
                data-readdy-form
                id="contact-inquiry-form"
                onSubmit={handleSubmit}
                className="bg-white rounded-2xl p-8 md:p-10 border border-[#e8e0d5] space-y-6"
              >
                {/* Name + Phone */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label
                      className="block text-[#5a4e45] text-[1rem] font-medium mb-2"
                      style={{ fontFamily: "'Noto Serif TC', serif" }}
                    >
                      姓名 <span className="text-[#c9a96e]">*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={form.name}
                      onChange={handleChange}
                      placeholder="請輸入您的姓名"
                      required
                      className={inputBase}
                      style={{ fontFamily: "'Noto Serif TC', serif", fontSize: "1rem" }}
                    />
                  </div>
                  <div>
                    <label
                      className="block text-[#5a4e45] text-[1rem] font-medium mb-2"
                      style={{ fontFamily: "'Noto Serif TC', serif" }}
                    >
                      聯絡電話
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={form.phone}
                      onChange={handleChange}
                      placeholder="請輸入您的電話"
                      className={inputBase}
                      style={{ fontFamily: "'Noto Serif TC', serif", fontSize: "1rem" }}
                    />
                  </div>
                </div>

                {/* Email */}
                <div>
                  <label
                    className="block text-[#5a4e45] text-[1rem] font-medium mb-2"
                    style={{ fontFamily: "'Noto Serif TC', serif" }}
                  >
                    電子郵件 <span className="text-[#c9a96e]">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={handleChange}
                    placeholder="請輸入您的電子郵件"
                    required
                    className={inputBase}
                    style={{ fontFamily: "'Noto Serif TC', serif", fontSize: "1rem" }}
                  />
                </div>

                {/* Inquiry Type */}
                <div>
                  <label
                    className="block text-[#5a4e45] text-[1rem] font-medium mb-2"
                    style={{ fontFamily: "'Noto Serif TC', serif" }}
                  >
                    諮詢類型 <span className="text-[#c9a96e]">*</span>
                  </label>
                  <div className="relative">
                    <select
                      name="inquiryType"
                      value={form.inquiryType}
                      onChange={handleChange}
                      required
                      className={`${inputBase} appearance-none pr-10 cursor-pointer`}
                      style={{ fontFamily: "'Noto Serif TC', serif", fontSize: "1rem" }}
                    >
                      <option value="" disabled>請選擇諮詢類型</option>
                      {inquiryTypes.map((t) => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </select>
                    <i className="ri-arrow-down-s-line absolute right-4 top-1/2 -translate-y-1/2 text-[#8b6f47] text-xl pointer-events-none"></i>
                  </div>
                </div>

                {/* Message */}
                <div>
                  <label
                    className="block text-[#5a4e45] text-[1rem] font-medium mb-2"
                    style={{ fontFamily: "'Noto Serif TC', serif" }}
                  >
                    訊息內容 <span className="text-[#c9a96e]">*</span>
                  </label>
                  <textarea
                    name="message"
                    value={form.message}
                    onChange={handleChange}
                    placeholder="請詳細描述您的需求或問題，例如：想了解哪種天珠適合自己、想訂製什麼樣的飾品等..."
                    required
                    rows={5}
                    maxLength={500}
                    className={`${inputBase} resize-none`}
                    style={{ fontFamily: "'Noto Serif TC', serif", fontSize: "1rem" }}
                  />
                  <div className="flex justify-between mt-1.5">
                    <span
                      className={`text-[1rem] ${charCount > 500 ? "text-red-500" : "text-[#a89880]"}`}
                      style={{ fontFamily: "'Noto Serif TC', serif" }}
                    >
                      {charCount > 500 ? "已超過字數限制" : ""}
                    </span>
                    <span
                      className={`text-[1rem] ${charCount > 500 ? "text-red-500" : "text-[#a89880]"}`}
                      style={{ fontFamily: "'Noto Serif TC', serif" }}
                    >
                      {charCount} / 500
                    </span>
                  </div>
                </div>

                {/* Error */}
                {formState === "error" && (
                  <p
                    className="text-red-500 text-[1rem]"
                    style={{ fontFamily: "'Noto Serif TC', serif" }}
                  >
                    送出失敗，請稍後再試或直接透過 LINE 聯繫我們。
                  </p>
                )}

                {/* Submit */}
                <button
                  type="submit"
                  disabled={formState === "submitting" || charCount > 500}
                  className="w-full py-4 bg-[#8b6f47] text-white rounded-xl text-[1rem] font-semibold tracking-widest cursor-pointer hover:bg-[#7a5f3a] transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap"
                  style={{ fontFamily: "'Noto Serif TC', serif" }}
                >
                  {formState === "submitting" ? (
                    <span className="flex items-center justify-center gap-2">
                      <i className="ri-loader-4-line animate-spin"></i>
                      傳送中...
                    </span>
                  ) : (
                    "傳送訊息"
                  )}
                </button>
              </form>
            )}
          </div>

          {/* Right: Contact Info */}
          <div
            className={`transition-all duration-1000 delay-200 ${
              visible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-8"
            }`}
          >
            <div className="flex items-center gap-3 mb-6">
              <span className="w-10 h-px bg-[#8b6f47]"></span>
              <span
                className="text-[#8b6f47] text-[1rem] tracking-[0.3em] font-medium"
                style={{ fontFamily: "'Noto Serif TC', serif" }}
              >
                REACH US
              </span>
            </div>
            <h2
              className="text-[#2d2520] font-bold mb-3 leading-[1.4]"
              style={{
                fontSize: "clamp(1.5rem, 3vw, 2.125rem)",
                fontFamily: "'Noto Serif TC', serif",
              }}
            >
              直接聯繫我們
            </h2>
            <p
              className="text-[#7a6a5a] leading-[1.75] mb-8"
              style={{
                fontSize: "1.1875rem",
                fontFamily: "'Noto Serif TC', serif",
              }}
            >
              偏好即時溝通？歡迎透過以下方式直接與我們聯繫，我們將盡快回覆您。
            </p>

            {/* Contact Cards */}
            <div className="space-y-4">
              {/* LINE */}
              <a
                href="https://line.me"
                rel="nofollow"
                target="_blank"
                className="flex items-center gap-5 p-6 bg-white rounded-2xl border border-[#e8e0d5] cursor-pointer hover:border-[#8b6f47] hover:-translate-y-0.5 transition-all duration-300 group"
              >
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#06C755]/10 flex-shrink-0">
                  <i className="ri-line-line text-[#06C755] text-2xl"></i>
                </div>
                <div className="flex-1">
                  <p
                    className="text-[#2d2520] font-semibold text-[1.1875rem] mb-0.5"
                    style={{ fontFamily: "'Noto Serif TC', serif" }}
                  >
                    LINE 即時諮詢
                  </p>
                  <p
                    className="text-[#7a6a5a] text-[1rem]"
                    style={{ fontFamily: "'Noto Serif TC', serif" }}
                  >
                    @longjing
                  </p>
                </div>
                <i className="ri-arrow-right-line text-[#8b6f47] text-xl transition-transform duration-300 group-hover:translate-x-1"></i>
              </a>

              {/* Email */}
              <a
                href="mailto:service@longjing.com.tw"
                className="flex items-center gap-5 p-6 bg-white rounded-2xl border border-[#e8e0d5] cursor-pointer hover:border-[#8b6f47] hover:-translate-y-0.5 transition-all duration-300 group"
              >
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#f0e8dc] flex-shrink-0">
                  <i className="ri-mail-line text-[#8b6f47] text-2xl"></i>
                </div>
                <div className="flex-1">
                  <p
                    className="text-[#2d2520] font-semibold text-[1.1875rem] mb-0.5"
                    style={{ fontFamily: "'Noto Serif TC', serif" }}
                  >
                    電子郵件
                  </p>
                  <p
                    className="text-[#7a6a5a] text-[1rem]"
                    style={{ fontFamily: "'Noto Serif TC', serif" }}
                  >
                    service@longjing.com.tw
                  </p>
                </div>
                <i className="ri-arrow-right-line text-[#8b6f47] text-xl transition-transform duration-300 group-hover:translate-x-1"></i>
              </a>

              {/* Instagram */}
              <a
                href="https://instagram.com"
                rel="nofollow"
                target="_blank"
                className="flex items-center gap-5 p-6 bg-white rounded-2xl border border-[#e8e0d5] cursor-pointer hover:border-[#8b6f47] hover:-translate-y-0.5 transition-all duration-300 group"
              >
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#fdf0f5] flex-shrink-0">
                  <i className="ri-instagram-line text-[#e1306c] text-2xl"></i>
                </div>
                <div className="flex-1">
                  <p
                    className="text-[#2d2520] font-semibold text-[1.1875rem] mb-0.5"
                    style={{ fontFamily: "'Noto Serif TC', serif" }}
                  >
                    Instagram
                  </p>
                  <p
                    className="text-[#7a6a5a] text-[1rem]"
                    style={{ fontFamily: "'Noto Serif TC', serif" }}
                  >
                    @longjing_energy
                  </p>
                </div>
                <i className="ri-arrow-right-line text-[#8b6f47] text-xl transition-transform duration-300 group-hover:translate-x-1"></i>
              </a>

              {/* Facebook */}
              <a
                href="https://facebook.com"
                rel="nofollow"
                target="_blank"
                className="flex items-center gap-5 p-6 bg-white rounded-2xl border border-[#e8e0d5] cursor-pointer hover:border-[#8b6f47] hover:-translate-y-0.5 transition-all duration-300 group"
              >
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#f0f4ff] flex-shrink-0">
                  <i className="ri-facebook-circle-line text-[#1877f2] text-2xl"></i>
                </div>
                <div className="flex-1">
                  <p
                    className="text-[#2d2520] font-semibold text-[1.1875rem] mb-0.5"
                    style={{ fontFamily: "'Noto Serif TC', serif" }}
                  >
                    Facebook
                  </p>
                  <p
                    className="text-[#7a6a5a] text-[1rem]"
                    style={{ fontFamily: "'Noto Serif TC', serif" }}
                  >
                    瓏靖手作能量
                  </p>
                </div>
                <i className="ri-arrow-right-line text-[#8b6f47] text-xl transition-transform duration-300 group-hover:translate-x-1"></i>
              </a>
            </div>

            {/* Response Time Note */}
            <div className="mt-6 p-5 bg-[#f5f0e8] rounded-xl flex items-start gap-3">
              <i className="ri-time-line text-[#8b6f47] text-xl mt-0.5 flex-shrink-0"></i>
              <p
                className="text-[#7a6a5a] leading-[1.75] text-[1rem]"
                style={{ fontFamily: "'Noto Serif TC', serif" }}
              >
                <strong className="text-[#2d2520]">回覆時間：</strong>
                LINE 通常在數小時內回覆；電子郵件與表單訊息將在 1–2 個工作天內回覆。
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
